import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTableColumnResize } from '../../../utils/tableColumnResize';
import { ResizableColgroup, ResizableTableHead } from '../../../components/ResizableTableHead';
import TablePagination, { TABLE_PAGE_SIZE, usePaginatedRows } from '../../../shared/components/TablePagination';

export default function DeploymentInventoryTable({
  columns,
  widthsKey,
  rows,
  emptyLabel,
  selectedKey,
  getRowKey,
  onSelectRow,
  renderRow,
}) {
  const {
    widths: columnWidths,
    onResizeStart,
    tableMinWidth,
    resizingKey,
  } = useTableColumnResize(columns, widthsKey);

  const tableHeaderRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [headerGutterPx, setHeaderGutterPx] = useState(8);

  const handleTableScroll = useCallback((event) => {
    if (tableHeaderRef.current) {
      tableHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  const list = Array.isArray(rows) ? rows : [];
  const tablePagination = usePaginatedRows(list, TABLE_PAGE_SIZE);
  const visibleRows = tablePagination.pageRows;

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const CONTENT_INSET_PX = 16;
    const syncGutter = () => {
      const scrollbarLane = Math.max(0, Math.round(el.offsetWidth - el.clientWidth));
      const next = scrollbarLane + CONTENT_INSET_PX;
      setHeaderGutterPx((prev) => (prev === next ? prev : next));
    };
    syncGutter();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncGutter) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncGutter);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncGutter);
    };
  }, [list.length]);

  const tableClassName =
    'assets-table assets-inventory-table assets-inventory-table--resizable risks-page-table risks-page-table--resizable';
  const tableStyle = { width: '100%', minWidth: tableMinWidth };
  const colCount = columns.length;

  return (
    <>
    <div className="assets-table-shell assets-table-shell--inventory-body risks-page-table-shell deployment-table-shell deployment-table-wrap">
      <div
        className="risks-page-table-header-frame"
        style={{ '--risks-header-gutter': `${headerGutterPx}px` }}
      >
        <div ref={tableHeaderRef} className="risks-page-table-header">
          <table className={`${tableClassName} assets-inventory-table--header risks-page-table--header`} style={tableStyle}>
            <ResizableColgroup columns={columns} widths={columnWidths} />
            <ResizableTableHead
              columns={columns}
              widths={columnWidths}
              onResizeStart={onResizeStart}
              resizingKey={resizingKey}
              resizeHandleClassName="risks-page-col-resize-handle"
            />
          </table>
        </div>
        <div className="risks-page-table-header-gutter" aria-hidden="true" />
      </div>
      <div
        ref={tableScrollRef}
        className="assets-inventory-table-scroll assets-inventory-table-scroll--body risks-page-table-scroll"
        onScroll={handleTableScroll}
      >
        <table className={`${tableClassName} risks-page-table--body`} style={tableStyle}>
          <ResizableColgroup columns={columns} widths={columnWidths} />
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="assets-empty-cell">{emptyLabel}</td>
              </tr>
            ) : (
              visibleRows.map((row) => {
                const key = getRowKey(row);
                const isSelected = selectedKey === key;
                return (
                  <tr
                    key={key}
                    className={[
                      'assets-row-clickable',
                      isSelected ? 'assets-row-selected is-selected' : null,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => onSelectRow(row)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onSelectRow(row);
                      }
                    }}
                    tabIndex={0}
                    aria-selected={isSelected}
                  >
                    {renderRow(row)}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
    <TablePagination {...tablePagination} itemLabel="rows" />
  </>
  );
}
